import React, { createContext, useState, useEffect } from "react";
import axiosInstance from "./axiosInstance";

const AuthContext = createContext();

// Always accessible regardless of plan
const ALWAYS_ALLOWED = [
  'profile', 'changepassword', 'plans', 'pricing', 'paymenthistory',
  'company', 'dashboard', 'emicalculator',
];

// Basic / Basic Trial: dealer, stocks, marketplace (collection) only.
// No billing, no reports, no finance, no user management.
const BASIC_PAGES = [
  'dashboard', 'profile', 'changepassword', 'plans', 'pricing', 'paymenthistory',
  'dealer', 'stocks',
];

// Starter: core finance + 3 basic reports. No exchange, seized, user mgmt, accounts.
const STARTER_PAGES = [
  ...ALWAYS_ALLOWED,
  'finance', 'addfinance', 'viewfinance', 'addreceipt', 'duereceipt',
  'collection', 'fullsettlement',
  'dealer', 'stocks',
  'billing', 'cancelbill', 'search_bill', 'buyer',
  // Reports menu + 3 basic report types only
  'reports', 'report_purchase', 'report_sales', 'report_stock',
];

// Professional: Starter + exchange, seized, user (3 users), all 5 reports, partial accounts
const PROFESSIONAL_PAGES = [
  ...STARTER_PAGES,
  'exchange', 'addexchange',
  'seizedvehicle',
  'user',
  // Remaining 2 report types
  'report_outstanding', 'report_collection',
  // Accounts menu + 3 sub-pages (no ledger, no P&L)
  'accounts', 'account_summary', 'account_collection', 'account_aging',
];

// Business: everything — null means no restriction (includes account_ledger, account_pl)
const PLAN_PAGE_MAP = {
  trial:        null,               // Full 14-day trial of everything
  basic:        BASIC_PAGES,
  starter:      STARTER_PAGES,
  professional: PROFESSIONAL_PAGES,
  business:     null,               // All pages — unlimited users, full accounts
};

function resolvePlanTier(planName) {
  if (!planName) return 'trial';
  const lower = planName.toLowerCase();
  if (lower.includes('business'))      return 'business';
  if (lower.includes('professional'))  return 'professional';
  if (lower.includes('starter'))       return 'starter';
  if (lower.includes('basic'))         return 'basic';
  return 'trial';
}

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser]                       = useState(null);
  const [loading, setLoading]                 = useState(true);
  const [permissions, setPermissions]         = useState([]);
  const [planPages, setPlanPages]             = useState(null); // null = no restriction yet
  const [planName, setPlanName]               = useState('');
  const [planEndDate, setPlanEndDate]         = useState('');

  const applyPlan = (name, endDate) => {
    const tier  = resolvePlanTier(name);
    const pages = PLAN_PAGE_MAP[tier]; // null for business
    setPlanName(name || '');
    setPlanPages(pages);
    setPlanEndDate(endDate || '');
  };

  const fetchPlan = async (company_id) => {
    if (!company_id) return;
    try {
      const res = await axiosInstance.get(`/payment/subscription/${company_id}`);
      const name    = res.data?.data?.plan_name  || '';
      const endDate = res.data?.data?.expiry_date || res.data?.data?.end_date || '';
      applyPlan(name, endDate);
    } catch (e) {
      // If subscription fetch fails, fall back to trial restrictions
      applyPlan('', '');
    }
  };

  useEffect(() => {
    const token        = localStorage.getItem("token");
    const refreshToken = localStorage.getItem("refreshToken");
    const user_id      = localStorage.getItem("user_id");
    const company_id   = localStorage.getItem("company_id");

    if (token && refreshToken && user_id && company_id) {
      const name  = localStorage.getItem("name");
      const email = localStorage.getItem("email");
      setIsAuthenticated(true);
      setUser({ name, email });

      Promise.all([
        axiosInstance.get(`/permission/user/${user_id}`),
        axiosInstance.get(`/payment/subscription/${company_id}`),
      ])
        .then(([permRes, subRes]) => {
          setPermissions(permRes.data.data || []);
          const sub = subRes.data?.data || {};
          applyPlan(sub.plan_name || '', sub.expiry_date || sub.end_date || '');
        })
        .catch((err) => {
          console.error("Failed to fetch permissions or plan:", err);
          // Still try permissions alone if plan fails
          axiosInstance.get(`/permission/user/${user_id}`)
            .then((r) => setPermissions(r.data.data || []))
            .catch(() => {});
          applyPlan('', '');
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (
    user_id, company_id, accessToken, refreshToken,
    name, email, user_type, profile_picture
  ) => {
    localStorage.setItem("user_id",          user_id);
    localStorage.setItem("company_id",       company_id);
    localStorage.setItem("token",            accessToken);
    localStorage.setItem("refreshToken",     refreshToken);
    localStorage.setItem("name",             name);
    localStorage.setItem("email",            email);
    localStorage.setItem("user_type",        user_type);
    localStorage.setItem("profile_picture",  profile_picture);

    setIsAuthenticated(true);
    setUser({ name, email });

    try {
      const [permRes, subRes] = await Promise.all([
        axiosInstance.get(`/permission/user/${user_id}`),
        axiosInstance.get(`/payment/subscription/${company_id}`),
      ]);
      setPermissions(permRes.data.data || []);
      const sub = subRes.data?.data || {};
      applyPlan(sub.plan_name || '', sub.expiry_date || sub.end_date || '');
    } catch (error) {
      console.error("Failed to fetch permissions or plan at login:", error);
      setPermissions([]);
      applyPlan('', '');
    }
  };

  const company = (userData) => {
    localStorage.setItem("companyname",   userData.company_name);
    localStorage.setItem("address1",      userData.company_address1);
    localStorage.setItem("address2",      userData.company_address2);
    localStorage.setItem("city",          userData.company_city);
    localStorage.setItem("Phone1",        userData.company_phone1);
    localStorage.setItem("phone2",        userData.company_phone2);
    localStorage.setItem("company_logo",  userData.logo_filename);
  };

  const logout = () => {
    localStorage.clear();
    setIsAuthenticated(false);
    setUser(null);
    setPermissions([]);
    setPlanPages(null);
    setPlanName('');
  };

  // Helper: is a page key allowed by the current plan?
  const isPlanAllowed = (pageKey) => {
    if (planPages === null) return true; // Business or not yet loaded: allow all
    return planPages.includes(pageKey);
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated, user, login, logout, company,
        loading, permissions, planPages, planName, planEndDate, isPlanAllowed,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
